import 'qtip2';

import { CancelToken } from 'axios';
import Numeral from 'numeral';
import urlJoin from 'url-join';

import {
    FEATURES_ZOOM_UPPER_THRESHOLD,
    MA_LAYER,
    MAP_MAX_ZOOM,
    MAP_SATELLITE_MIN_ZOOM,
    MARKER_SIZE,
    NONE_TYPE,
    REALTOR_FEATURE_ID,
    REFERENCE_FEATURE_ID,
    RENTAL_PRICE_TYPE,
    SALE_PRICE_TYPE,
} from '../common/constants.es6';
import { referenceMessage } from '../common/contact';
import { hasTouchEvents, isMobile } from '../common/device';
import Ga from '../common/ga';
import MAEvent from '../common/ma-event';
import {
    getActivePriceDate,
    getAvivIdFromLegacy,
    getGeoLayerMetadata,
    getLinkedAddresses,
    getPlaceData,
    getPriceLayerMetadata,
} from '../common/map/aviv-api';
import {
    AVIV_TYPE_TO_LEGACY,
    AVIV_TYPE_TO_PLACE_TYPE,
    FRANCE_BOUNDARY_ID,
    FRANCE_GEO_CONFIG,
    getAvivBoundaryFromPlace,
    parseAvivFeatureId,
} from '../common/map/aviv-config';
import {
    addCurrentPlaceOutline,
    addGeoSelectionLayers,
    addGeoWithinSelectionLayers,
    addPriceLayersFromMetadata,
    getSelectableLayers,
    removeAvivLayers,
    updateHoverState,
} from '../common/map/aviv-layers';
import { extractLegendData, hideAvivLegend, updateAvivLegend } from '../common/map/aviv-legend';
import {
    boundsFromBox,
    getBoundsString,
    parseStringifiedFeatures,
    shouldChangeBounds,
} from '../common/map/helpers.es6';
import MapboxGL from '../common/map/mapbox-gl-loader.es6';
import { patchMapStyle, pricesMapStyle } from '../common/map/styles';
import Navigation from '../common/navigation';
import HTTPService from '../common/services/HTTPService.es6';
import spinnerTemplate from '../common/templates/spinner-dots.html';
import { EstateType, thorFormatPlaceName, thorPushEvent } from '../common/thor';
import { addURLParams, deepEqual, getValueFromPath, isIE, isOldFirefox } from '../common/utils';
import Wa from '../common/wa';
import addressListTemplate from '../templates/address_list.html';
import featuresControlTemplate from '../templates/map-features-control.html';
import tilesControlTemplate from '../templates/map-prices-tile-control.html';
import popupSettingsFeaturesTemplate from '../templates/map-settings-features.html';
import popupSettingsTilesTemplate from '../templates/map-settings-tiles.html';
import pricesInfowindowTemplate from '../templates/prices-infowindow.html';

/**
 * Max marker count by layer on mobile.
 * @type {number}
 */
const DATA_SOURCE_CACHE_SIZE_MOBILE = 100;

/**
 * Max marker count by layer on desktop.
 * @type {Number}
 */
const DATA_SOURCE_CACHE_SIZE_DESKTOP = 300;

/**
 * Max version of Firefox with bad support of WebGL.
 * @type {Boolean}
 */
const OLD_FIREFOX = isOldFirefox();

/**
 * Dynamic GeoJSON layers added at runtime (not from style JSON).
 * Used to exclude these from satellite mode visibility toggling.
 * @type {Array}
 */
const MA_LAYERS = ['parcelle-selected', 'place-selected', 'place-hover'];

/**
 * Check if a layer source belongs to AVIV tile layers (should not be hidden in satellite mode)
 * @param {string} source - Layer source name
 * @returns {boolean} True if it's an AVIV layer
 */
function isAvivLayerSource(source) {
    if (!source) return false;
    return (
        source.startsWith('prices-') ||
        source.startsWith('selection-') ||
        source.startsWith('current-place-outline-') ||
        source.startsWith('aviv-geo-') ||
        source.startsWith('geo-fill-') ||
        source.startsWith('geo-border-')
    );
}

/**
 * Find first parent boundary with price layer metadata.
 * Uses reduce pattern to avoid for...of with await (ESLint no-await-in-loop).
 *
 * @param {Array} parents - Array of parent objects with id property
 * @param {string} transactionType - Transaction type for price query
 * @param {string} priceDate - Price date for query
 * @returns {Promise<{metadata: Array, boundaryId: string}|null>} First parent with metadata or null
 */
async function findParentWithMetadata(parents, transactionType, priceDate) {
    // Use reduce to sequentially check parents until we find one with data
    return parents.reduce(async (prevPromise, parent) => {
        const prev = await prevPromise;
        if (prev) return prev; // Already found data, skip remaining

        const metadata = await getPriceLayerMetadata(transactionType, parent.id, priceDate);
        if (metadata && metadata.length > 0) {
            return { metadata, boundaryId: parent.id };
        }
        return null;
    }, Promise.resolve(null));
}

// Child price data types that indicate a city has its own price data (not just boundary-level)
const CHILD_PRICE_DATA_TYPES = ['STU3', 'HONU', 'BLOC', 'PARC', 'BILD'];

// City-level boundary types that may need fallback to parent
const CITY_BOUNDARY_TYPES = ['AD08', 'AD09'];

// Place types not supported in RENT mode (only STU3 and admin boundaries are valid)
const INVALID_RENT_PLACE_TYPES = ['BILD', 'PARC', 'BLOC'];

// Sub-city geotypes that should use the parent city's boundary for price layers
const SUB_CITY_GEOTYPES = ['address', 'street', 'borough', 'subcity', 'iris', 'arrmun'];

// Check if metadata contains child price data (STU3/HONU/BLOC etc)
function hasChildPriceData(metadata) {
    if (!metadata || metadata.length === 0) return false;
    return metadata.some(layer => CHILD_PRICE_DATA_TYPES.includes(layer?.place?.type));
}

// Check if a city-level boundary needs fallback to parent (no child price data)
function cityNeedsFallback(boundaryId, metadata) {
    const boundaryType = boundaryId?.substring(0, 4);
    if (!CITY_BOUNDARY_TYPES.includes(boundaryType)) return false;
    return !hasChildPriceData(metadata);
}

/**
 * Get a promise for the legacy ID lookup, or null if not needed.
 * This allows parallelization with other API calls.
 * @param {Object} place - Place object with geotype_label, id, city_id
 * @returns {Promise<string|null>|null} Promise resolving to AVIV ID, or null if lookup not needed
 */
function getLegacyIdLookupPromise(place) {
    const { geotype_label: geotype, id: legacyId, city_id: cityId } = place || {};

    if (geotype === 'country' || !legacyId) {
        return null; // No lookup needed, caller should use FRANCE_BOUNDARY_ID
    }

    if (SUB_CITY_GEOTYPES.includes(geotype) && cityId) {
        return getAvivIdFromLegacy('city', cityId);
    }

    return getAvivIdFromLegacy(geotype, legacyId);
}

/**
 * Resolve the final boundary ID from API result and place data.
 * Applies all fallback logic in a consistent way.
 * @param {string|null} avivIdFromApi - Result from legacy ID lookup API
 * @param {Object} place - Place object with geotype_label, id, city_id
 * @returns {string} Final AVIV boundary ID
 */
function resolveBoundaryId(avivIdFromApi, place) {
    const { geotype_label: geotype, id: legacyId, city_id: cityId } = place || {};

    // Country or missing ID -> France
    if (geotype === 'country' || !legacyId) {
        return FRANCE_BOUNDARY_ID;
    }

    // Sub-city types use city boundary
    if (SUB_CITY_GEOTYPES.includes(geotype) && cityId) {
        return avivIdFromApi || `AD08FR${cityId}`;
    }

    // Standard case: API result -> place-based fallback -> France fallback
    return avivIdFromApi || getAvivBoundaryFromPlace(place) || FRANCE_BOUNDARY_ID;
}

const SELECTED_FEATURE_GEO_TYPE = ['address', 'street', 'borough'];

/**
 * Check Internet Explorer version.
 * @type {Boolean}
 */
const IS_IE = isIE();

const LOADING_WRAPPER_ACTIVE = 'loading-wrapper--loading';
let trackingDataList = [];

/**
 * Retrieve URL for features.
 * @param {String} layerId
 * @param {String} bounds
 * @return {String} Url for fetching features markers
 */
function getURLForFeatures(layerId, bounds) {
    switch (layerId) {
        case REFERENCE_FEATURE_ID:
            return HTTPService.getMapFeatureURL('/carte/biens-vendus', bounds);
        case REALTOR_FEATURE_ID:
            return HTTPService.getMapFeatureURL('/carte/agences', bounds);
        default:
            return null;
    }
}

/**
 * Display the popup to control settings on mobile
 * @param {String} src ID selector of the popup content div
 */
function openSettingsPopup(src) {
    $.magnificPopup.open(
        {
            items: {
                type: 'inline',
                src,
            },
            fixedContentPos: 'fixed',
            callbacks: {
                open() {
                    // Ajax content is loaded and appended to DOM
                    Navigation.disablePageScroll();

                    $.event.trigger({
                        type: MAEvent.mapReferencePopinOpened,
                    });
                },
                close() {
                    Navigation.enablePageScroll();
                },
            },
        },
        0,
    );
}

/**
 * Hide the popup to control settings on mobile
 */
function closeSettingsPopup() {
    $.magnificPopup.close();
}

/**
 * Load and display the popin for the past sale / contact form
 * @param {String} src Url for the content
 * @param {String} gaTagFrom Suffix to send to URL param `ga_tag`
 */
function loadReferencePopin(src, gaTagFrom) {
    $.magnificPopup.open(
        {
            type: 'ajax',
            fixedContentPos: 'fixed',
            items: {
                src: addURLParams(src, {
                    ga_category: MA.Context.gaCategory,
                    ga_label_prefix: `${MA.Context.gaLabelPrefix}:past_sale_popin`,
                    wa_name_prefix: `${MA.Context.gaLabelPrefix}:past_sale_popin`,
                    wa_source: MA.Context.waSource || MA.Context.gaLabelPrefix,
                    wa_source_deprecated: `${MA.Context.mapGaLabelPrefix}:past_sale`,
                    ga_tag: `from:prices:map:${gaTagFrom}`,
                    source: 'REALTOR_MESSAGE_SOURCES.PRICES',
                }),
            },
            callbacks: {
                ajaxContentAdded() {
                    // Ajax content is loaded and appended to DOM
                    Navigation.disablePageScroll();
                    const $form = $(this.content).find('[data-message-form]');

                    $.event.trigger({
                        type: MAEvent.mapReferencePopinOpened,
                        $form,
                    });

                    referenceMessage(
                        this.content,
                        {
                            category: MA.Context.mapGaCategory,
                            actionPrefix: 'past_sale:popin',
                            label: `from:${MA.Context.mapGaLabelPrefix}:${gaTagFrom}`,
                        },
                        {
                            key: 'form_success',
                            name: 'realtor_contact',
                            data: `source=${Wa.updateSourceWhenRetargeting(
                                MA.Context.mapGaLabelPrefix,
                            )}:past_sales|realtor_id=${$form.data(
                                'realtor-id',
                            )}|reference_id=${$form.data('reference-id')}`,
                        },
                        {
                            key: 'page_view:past_sale',
                            name: `${MA.Context.mapGaLabelPrefix}:past_sales`,
                            data: `source=${MA.Context.waSource ||
                                MA.Context.gaLabelPrefix}|realtor_id=${$form.data(
                                'realtor-id',
                            )}|reference_id=${$form.data('reference-id')}`,
                        },
                    );
                },
                close() {
                    Navigation.enablePageScroll();
                },
            },
        },
        0,
    );
}

/**
 * Open reference as a popin, not as a new page.
 * @param {Event} event Event
 */
function openReferencePopin(event) {
    event.preventDefault();
    loadReferencePopin(
        $(event.currentTarget).attr('href'),
        $(event.currentTarget).data('ga-tag-from'),
    );
}

export default class VectorMap {
    /**
     * ID of overall container
     */
    wrapperDataAttribute = '[data-vector-map]';

    /**
     * jQuery wrapper for this.wrapperDataAttribute
     * @type {jQuery}
     */
    $wrapper = null;

    /**
     * Collection of all features
     * @type {Object}
     */
    dataSourceCache = {};

    /**
     * ID on DOM for div receiving the Mapbox map canvas
     */
    containerId = 'mapbox_map';

    /**
     * jQuery container for containerId
     * @type {jQuery}
     */
    $container = null;

    /**
     * Current place
     * @type {object}
     */
    place = {};

    /**
     * Current place market price
     * @type {object}
     */
    placePrices = {};

    /**
     * Mapbox map instance
     * @type {MapboxGL.Map}
     */
    map = null;

    /**
     * Mapbox popup instance for markers
     * @type {MapboxGL.Popup}
     */
    markerPopup = null;

    /**
     * Axios cancelling
     * @type {Function}
     */
    cancelPopupRequest = null;

    /**
     * Mapbox popup instance for address list
     * @type {MapboxGL.Popup}
     */
    addressPopup = null;

    /**
     * Info window for mobile
     * @type {jQuery}
     */
    $infoWindow = $('[data-map-infowindow]');

    /**
     * List of tiles layers (static - no longer fetched from market API)
     * @type {Object}
     */
    toggleableTilesLayers = {
        [NONE_TYPE]: {
            tilejson: {
                name: NONE_TYPE,
                description: 'Aucun',
            },
        },
        [SALE_PRICE_TYPE]: {
            tilejson: {
                name: SALE_PRICE_TYPE,
                description: 'Prix au m2',
            },
        },
        [RENTAL_PRICE_TYPE]: {
            tilejson: {
                name: RENTAL_PRICE_TYPE,
                description: 'Loyer au m2',
            },
        },
    };

    currentFeaturesLayers = new Set();

    /**
     * List of features symbols layers (static - no longer fetched from market API)
     * @type {Object}
     */
    toggleableFeaturesLayers = {
        realtor: {
            tilejson: {
                name: 'realtor',
                description: 'Agences',
            },
        },
        reference: {
            tilejson: {
                name: 'reference',
                description: 'Biens vendus',
            },
        },
    };

    /**
     * Save the current toggled tile layer
     */
    priceType = SALE_PRICE_TYPE;

    /**
     * Wrapper of tiles layers legends
     * @type {*}
     */
    mapLegendSelector = '[data-map-legend]';

    $mapLegend = null;

    /**
     * @type {boolean}
     */
    satelliteStyleActive = false;

    /**
     * The use has changed any toggle of features ?
     * @type {boolean}
     */
    userChangedFeatures = false;

    styleIsLoading = false;

    /**
     * Save the bounds to check if bounds really changed on zoomend event
     * This is a workaround for a bug
     * @type {MapboxGL.Bounds}
     */
    currentBounds = null;

    /**
     * Current AVIV boundary place ID for price context
     * @type {string}
     */
    avivBoundaryPlaceId = FRANCE_BOUNDARY_ID;

    /**
     * AVIV price date (fetched from API or fallback)
     * @type {string}
     */
    avivPriceDate = null;

    /**
     * Selectable layers for AVIV mode
     * @type {Array}
     */
    avivSelectableLayers = [];

    /**
     * Current AVIV price layer IDs
     * @type {Array}
     */
    avivPriceLayerIds = [];

    /**
     * Current AVIV outline layer IDs
     * @type {Array}
     */
    avivOutlineLayerIds = [];

    /**
     * Current AVIV layer metadata (after zoom adjustment)
     * @type {Array}
     */
    avivLayerMetadata = [];

    /**
     * AVIV geo layer metadata (from tile-api)
     * @type {Array}
     */
    avivGeoMetadata = [];

    /**
     * AVIV geo layer IDs (from tile-api, for selection)
     * @type {Array}
     */
    avivGeoLayerIds = [];

    /**
     * Last hovered AVIV layer ID (for clearing hover state)
     * @type {string|null}
     */
    lastAvivHoverLayerId = null;

    /**
     * Current AVIV selection level (geo type like AD04, AD06, AD08)
     * Used to determine which geo layer is selectable for "outside" areas
     * @type {string|null}
     */
    avivSelectionLevel = null;

    /**
     * Current AVIV selection boundary ID (e.g., AD06FR36)
     * This may differ from avivBoundaryPlaceId when a city falls back to its parent subregion.
     * Used to skip unnecessary layer reloads when the effective selection doesn't change.
     * @type {string|null}
     */
    avivSelectionBoundaryId = null;

    /**
     * Timeout ID for debouncing AVIV layer reload operations
     * @type {number}
     */
    avivLayerReloadTimeout = 0;

    /**
     * Cache for price metadata per boundary ID
     * Key: `${boundaryId}:${transactionType}`, Value: metadata array
     * @type {Map<string, Array>}
     */
    avivPriceMetadataCache = new Map();

    /**
     * Max entries in avivPriceMetadataCache before evicting oldest
     */
    avivPriceMetadataCacheLimit = 50;

    /**
     * Timeout ID for debouncing window resize
     * @type {number}
     */
    avivResizeTimeout = 0;

    /**
     * Create instance of Mapbox Map, bind events and return it
     * @param {object} place
     * @param {object} placePrices
     * @return {MapboxGL.Map}
     */
    constructor(place, placePrices) {
        this.place = place;
        this.placePrices = placePrices;
        this.$mapLegend = $(this.mapLegendSelector);
        this.$wrapper = $(this.wrapperDataAttribute);
        this.$container = $(`#${this.containerId}`);

        if (!MapboxGL) {
            $('[data-map-nowebgl-warning]').show();
            return null;
        }

        this.$wrapper.addClass(LOADING_WRAPPER_ACTIVE);

        const placeBounds = boundsFromBox(this.place.viewport);

        // Build map options
        const mapOptions = {
            maxZoom: MAP_MAX_ZOOM,
            container: this.containerId, // container id
            center: placeBounds.getCenter(),
            // Mapbox v3 compatibility: preserve v1 pitch behavior
            maxPitch: 60, // v3 default is 85, we want v1 default of 60
            touchPitch: false, // disable pitch via touch gestures
            pitchWithRotate: false, // disable pitch when rotating
            // From mapbox support:
            // One of the bug fixes included in the v46 release involves
            // cross-source collision detection. The gl symbol layers (the blue and red dots)
            // added are from a different source than the text labels.
            // This results an overlap between layers
            // https://github.com/mapbox/mapbox-gl-js/releases/tag/v0.46.0
            crossSourceCollisions: false,
        };

        // Create instance
        this.map = new MapboxGL.Map(mapOptions);

        this.setStyle(patchMapStyle(pricesMapStyle));

        if (isMobile()) {
            // Click to open setting popup
            $('[data-map-settings-close]').on('click', event => {
                event.preventDefault();
                closeSettingsPopup();
            });
            $('[data-settings-button]').on('click', event => {
                event.preventDefault();
                openSettingsPopup(event.currentTarget.getAttribute('href'));
            });
        } else {
            // Add zoom controls to the map.
            this.map.addControl(new MapboxGL.NavigationControl());
        }
        this.addSatelliteButton();
        // Disable map 3D rotation using right click + drag
        this.map.dragRotate.disable();
        // Disable map rotation using touch rotation gesture
        this.map.touchZoomRotate.disableRotation();
        // When map is loaded and ready to interact with
        this.map.on('load', this.onLoad.bind(this));

        // Set the viewport of the map against the place viewport
        this.map.fitBounds(placeBounds, { animate: false });
        this.waitForStylesLoaded().then(() => this.toggleSatelliteStyleOnZoom());
    }

    onLoad() {
        this.currentBounds = this.map.getBounds();

        /**
         * Create UI to control the Map
         */
        this.createToggleFeatures();
        this.createToggleTiles();
        this.createPopupSettingsFeatures();

        /**
         * Register and display the layers
         */
        this.addLayers();

        // Initialize AVIV tile sources
        this.avivInitialized = false; // Will be set true when init completes
        this.initAvivTileSources();

        /**
         * Bind user interactions events
         */

        // When user hover on symbol layer
        if (hasTouchEvents()) {
            this.bindTouchEvents();
        } else {
            this.bindMouseEvents();
        }

        // Wait for features loaded
        this.fetchAllFeatures(getBoundsString(this.currentBounds)).then(() => {
            // When user finished zooming interaction on map
            this.map.on('zoomend', () => this.onBoundsChanged());
            this.map.on('zoomend', () => this.toggleSatelliteStyleOnZoom());
            // When user finished moving (pan) the map
            this.map.on('moveend', () => this.onBoundsChanged());
        });

        // Fix #4: Add debounced window resize handler (200ms delay like aviv_price-pages)
        window.addEventListener('resize', () => {
            if (this.avivResizeTimeout) {
                clearTimeout(this.avivResizeTimeout);
            }
            this.avivResizeTimeout = setTimeout(() => {
                this.map.resize();
                this.map.triggerRepaint();
                this.avivResizeTimeout = 0;
            }, 200);
        });

        $(window).on(MAEvent.pricesPlaceDidChange, event => {
            this.onPlaceChanged(
                event.place,
                event.placePrices,
                event.updateMap,
                event.avivFeatureId,
                event.isMapClick,
            );
        });
        this.onPlaceChanged(this.place, this.placePrices, true);

        // Analytics initialisation
        this.setTracking();

        /**
         * When reload styles, all layers previously added has gone.
         * So we must re-add
         */
        this.map.on('style.load', () => {
            this.addLayers();
            this.fetchAllFeatures(getBoundsString(this.map.getBounds()));

            this.setSelection({
                layer: {
                    'source-layer': this.place.geotype_label,
                },
                properties: this.place,
            });
        });

        this.$wrapper.removeClass(LOADING_WRAPPER_ACTIVE);

        this.initializePriceInfoWindow();

        $.event.trigger({
            type: MAEvent.mapReady,
            place: this.place,
        });
    }

    /**
     * Specific events for touch devices
     */
    bindTouchEvents() {
        const generateMouseclick = layerId => event => {
            event.originalEvent.preventDefault();
            this.loadMarkerInfoWindow(event.features[0], layerId);
        };

        if (isMobile()) {
            // Prices infowindow should only be displayed on small devices
            this.map.on('dragend', () => {
                this.showPricesInfoWindow(this.place, this.placePrices);
            });
        } else {
            this.map.on('dragend', () => {
                this.hideMarkerInfoWindow();
            });
        }

        Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
            this.map.on('click', layerId, generateMouseclick(layerId));
        });

        this.map.on('click', this.tileClickEventHandler.bind(this));

        // Both behaviors are needed for handling iPad (who has touched but desktop infowindow)
        this.$container.on('click', '[data-popin-button]', openReferencePopin);
        this.$infoWindow.on('click', '[data-popin-button]', openReferencePopin);
    }

    /**
     *
     */
    bindMouseEvents() {
        const layersOver = Object.keys(this.toggleableFeaturesLayers || {}).map(
            layerId => `${layerId}-hover`,
        );

        // Bind mouse event on features layers
        // Show an popup when hovering feature
        Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
            this.map.on('mouseenter', layerId, event => {
                const feature = event.features[0];

                this.map.setFilter(`${layerId}-hover`, ['==', 'id', feature.properties.id]);
                this.loadMarkerInfoWindow(feature, layerId);
            });
        });

        // Force remove popup and over styles when hovering map
        this.map.on('mousemove', event => {
            const width = MARKER_SIZE; // Marker size
            const height = MARKER_SIZE; // Marker size
            const features = this.map.queryRenderedFeatures(
                [
                    [event.point.x - width / 2, event.point.y - height / 2],
                    [event.point.x + width / 2, event.point.y + height / 2],
                ],
                {
                    layers: layersOver.concat(Object.keys(this.toggleableFeaturesLayers || {})),
                },
            );

            if (!features || features.length === 0) {
                Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
                    this.map.setFilter(`${layerId}-hover`, ['==', 'id', '']);
                });
                this.hideMarkerInfoWindow();
            }
        });

        // Handle specific click on past sale/realtor/program marker
        this.map.on('click', event => {
            const features = this.map.queryRenderedFeatures(event.point, {
                layers: layersOver.concat(Object.keys(this.toggleableFeaturesLayers || {})),
            });
            if (!features || features.length === 0) {
                return;
            }

            switch (features[0].source) {
                case REALTOR_FEATURE_ID:
                    window.location = features[0].properties.url;
                    break;
                case REFERENCE_FEATURE_ID:
                    loadReferencePopin(features[0].properties.url, 'past_sale_marker');
                    break;
                default:
                    break;
            }
        });

        this.map.on('click', () => this.hideListAddressInfoWindow());
        this.map.on('movestart', () => this.hideListAddressInfoWindow());

        this.$container.on('click', '[data-popin-button]', openReferencePopin);

        if (!OLD_FIREFOX) {
            /**
             * Event for prices tiles layers
             */
            this.map.on('click', this.tileClickEventHandler.bind(this));

            this.map.on('mousemove', e => {
                if (this.avivSelectableLayers.length > 0) {
                    const currentZoom = this.map.getZoom();
                    const isRentMode = this.priceType === RENTAL_PRICE_TYPE;

                    const features = this.map.queryRenderedFeatures(e.point, {
                        layers: this.avivSelectableLayers,
                    });

                    const isFrench = f => f.properties?.feature_id?.includes('FR');
                    const getFeatureType = f => f.properties?.feature_id?.substring(0, 4);
                    const RENT_NON_HOVERABLE_TYPES = ['BILD', 'PARC', 'BLOC'];
                    const isRentHoverable = f =>
                        !RENT_NON_HOVERABLE_TYPES.includes(getFeatureType(f));

                    let frenchFeature;
                    if (isRentMode) {
                        // RENT mode: Only hover STU3 and above
                        frenchFeature = features.find(f => isFrench(f) && isRentHoverable(f));
                    } else if (currentZoom >= 15) {
                        // SELL mode at high zoom: Only hover PARC/BILD
                        const isPARCorBILD = f => {
                            const type = getFeatureType(f);
                            return type === 'PARC' || type === 'BILD';
                        };
                        frenchFeature = features.find(f => isFrench(f) && isPARCorBILD(f));
                    } else {
                        frenchFeature = features.find(isFrench);
                    }

                    this.map.getCanvas().style.cursor = frenchFeature ? 'pointer' : '';
                    this.updateAvivHover(frenchFeature || null);
                }
            });

            this.map.on('mouseout', () => {
                this.updateAvivHover(null);
            });

            this.map.on('zoom', () => {
                this.updateAvivHover(null);
            });
        }
    }

    tileClickEventHandler(event) {
        // Do nothing if a popup is shown
        const layersOver = Object.keys(this.toggleableFeaturesLayers || {}).map(
            layerId => `${layerId}-hover`,
        );
        const touchZone = [
            [event.point.x - MARKER_SIZE / 2, event.point.y - MARKER_SIZE / 2],
            [event.point.x + MARKER_SIZE / 2, event.point.y + MARKER_SIZE / 2],
        ];
        const markers = this.map.queryRenderedFeatures(touchZone, {
            layers: layersOver.concat(Object.keys(this.toggleableFeaturesLayers || {})),
        });
        if (markers.length > 0) {
            return;
        }

        const features = this.map.queryRenderedFeatures(event.point, {
            layers: this.avivSelectableLayers,
        });

        const isRentMode = this.priceType === RENTAL_PRICE_TYPE;
        const isFrench = f => f.properties?.feature_id?.includes('FR');
        const getFeatureType = f => f.properties?.feature_id?.substring(0, 4);
        const RENT_NON_CLICKABLE_TYPES = ['BILD', 'PARC', 'BLOC'];
        const isRentClickable = f => !RENT_NON_CLICKABLE_TYPES.includes(getFeatureType(f));

        // Find clickable feature based on mode
        let feature;
        if (isRentMode) {
            // RENT mode: Only click STU3 and above
            feature = features.find(f => isFrench(f) && isRentClickable(f));
        } else {
            feature = features.find(isFrench);
        }

        if (!feature) {
            this.map
                .getSource('place-selected')
                .setData({ type: 'FeatureCollection', features: [] });
            return;
        }

        // Handle AVIV features - convert to legacy format and trigger URL navigation
        if (feature.properties.feature_id && !feature.properties.geotype_label) {
            const featureId = feature.properties.feature_id;

            // Parse AVIV feature ID to get type (e.g., "AD08FR75056" -> "AD08", "PARCFR123" -> "PARC")
            const parsed = parseAvivFeatureId(featureId);
            const avivType =
                parsed?.type || featureId.match(/^(PARC|BILD|BLOC|STU3|[A-Z]+\d+)/)?.[0];
            const legacyGeotype = AVIV_TYPE_TO_LEGACY[avivType];

            // Check if this place type is "active" (has a price page) using FRANCE_GEO_CONFIG
            // Active types: region, subregion, city, arrmun - full navigation + layer reload
            // Inactive types: iris, parcelle, building, block - just zoom, no navigation
            const placeType = AVIV_TYPE_TO_PLACE_TYPE[avivType];
            const isActive = placeType && FRANCE_GEO_CONFIG[placeType]?.active;

            // For inactive types, handle based on legacy behavior:
            // - parcelle/building: fetch addresses and navigate
            // - iris, block: just zoom to viewport
            if (!isActive) {
                // Parcelle or building: fetch addresses
                if (legacyGeotype === 'parcelle' || legacyGeotype === 'building') {
                    // Update the outline to show the clicked parcelle/building
                    this.updateSelectedPlaceOutline(featureId, avivType);

                    getLinkedAddresses(featureId).then(addresses => {
                        if (!addresses || addresses.length === 0) {
                            // Fall back to just zooming
                            getPlaceData(featureId).then(place => {
                                if (place && place.viewport) {
                                    const bounds = [
                                        [place.viewport.sw.lng, place.viewport.sw.lat],
                                        [place.viewport.ne.lng, place.viewport.ne.lat],
                                    ];
                                    this.map.fitBounds(bounds, { padding: 20 });
                                }
                            });
                            return;
                        }

                        if (addresses.length === 1) {
                            // Single address - navigate directly
                            const $tempLink = $('<a>')
                                .attr('data-linked-place', '')
                                .attr('data-linked-address', '')
                                .attr('data-url', addresses[0].url)
                                .appendTo('[data-prices-map]');
                            $tempLink.trigger('click');
                            $tempLink.remove();
                        } else {
                            // Multiple addresses - show popup
                            this.showListAddressInfoWindow({ children: addresses }, event.lngLat);
                        }
                    });
                    return;
                }

                // Other inactive types (iris, block): just zoom to viewport
                getPlaceData(featureId).then(place => {
                    if (place && place.viewport) {
                        const bounds = [
                            [place.viewport.sw.lng, place.viewport.sw.lat],
                            [place.viewport.ne.lng, place.viewport.ne.lat],
                        ];
                        this.map.fitBounds(bounds, { padding: 20 });

                        // For IRIS, enforce minimum zoom after fitBounds completes (legacy behavior)
                        // For IRIS, enforce minimum zoom after fitBounds
                        if (legacyGeotype === 'iris') {
                            const MIN_ZOOM_IRIS = 15;
                            const handler = () => {
                                if (this.map.getZoom() < MIN_ZOOM_IRIS) {
                                    this.map.zoomTo(MIN_ZOOM_IRIS);
                                }
                                this.map.off('zoomend', handler);
                            };
                            this.map.on('zoomend', handler);
                        }
                    }

                    $.event.trigger({
                        type: MAEvent.mapPlaceClicked,
                        place: {
                            geotype_label: legacyGeotype,
                            slug: place?.slug || place?.name,
                        },
                        lngLat: event.lngLat,
                        zoom: this.map.getZoom(),
                    });

                    $.event.trigger({
                        type: 'gaSendFitBounds',
                        placeType: legacyGeotype,
                    });
                });
                return;
            }

            // Active place types - full navigation with layer reload
            // Hide existing layers immediately to prevent flicker during zoom.
            // reloadAvivLayersForPlace will either show them back (if same selection boundary)
            // or replace them with new layers.
            this.hideAvivPriceLayers();

            // Fetch place data from Place API to get viewport and slug
            getPlaceData(featureId).then(place => {
                if (place && place.viewport) {
                    const bounds = [
                        [place.viewport.sw.lng, place.viewport.sw.lat],
                        [place.viewport.ne.lng, place.viewport.ne.lat],
                    ];
                    this.map.fitBounds(bounds, { padding: 20 });
                }

                // Trigger mapPlaceClicked for URL navigation
                $.event.trigger({
                    type: MAEvent.mapPlaceClicked,
                    place: {
                        geotype_label: legacyGeotype,
                        slug: place?.slug || place?.name,
                        avivFeatureId: featureId, // Store AVIV ID for back navigation
                        isMapClick: true, // Flag to tell onPlaceChanged to reload layers
                    },
                    lngLat: event.lngLat,
                    zoom: this.map.getZoom(),
                });

                $.event.trigger({
                    type: 'gaSendFitBounds',
                    placeType: legacyGeotype,
                });
            });
        }
    }

    /**
     * When user hover a feature marker (colored dot) on symbol layer, fetch popup content
     * @param {Object} feature
     */
    loadMarkerInfoWindow(feature) {
        if (isMobile()) {
            this.updateInfoWindowContent(spinnerTemplate({ classes: 'spinner--dots' }), false);
            this.fetchPopupContent(feature).then(response =>
                this.updateInfoWindowContent(response.data.infowindow),
            );
            this.$infoWindow.trigger({
                type: MAEvent.mapFeatureInfoWindowOpened,
                feature,
            });
            return;
        }

        // Else Desktop
        this.map.getCanvas().style.cursor = 'pointer';
        if (!this.markerPopup) {
            this.markerPopup = new MapboxGL.Popup({
                closeOnClick: false,
                maxWidth: 'none', // new mapbox-gl version use a default maxWidth of 240px
            });
        }
        this.markerPopup
            .setLngLat(feature.geometry.coordinates)
            .setHTML(spinnerTemplate({ classes: 'spinner--dots' }))
            .addTo(this.map);

        if (this.cancelPopupRequest) {
            this.cancelPopupRequest();
        }
        this.popupCall = this.fetchPopupContent(feature).then(response => {
            if (!this.markerPopup) {
                return; // Popup has been hide during request
            }
            const { infowindow, trackingData } = response.data;
            if (trackingData) {
                trackingDataList = [];
                trackingData.forEach((item, index) => {
                    const { data, type } = item;
                    const isReference = type === 'reference';
                    const eventData = {
                        av_category: 'price_map',
                        index,
                        ...(isReference && {
                            av_estate_type_global: EstateType[data.item_type.key],
                            av_distribution_type_global: '2',
                        }),
                        av_items: [
                            {
                                list_name: 'price_map_infowindow_on_map',
                                id: data.id.toString(),
                                publication_id: 'MA',
                                name: isReference ? 'sold_property' : 'agency',
                                zip_code: isReference ? data.zip : data.zipcode,
                                country: 'france',
                                city: thorFormatPlaceName(data.city.name),
                                ...(isReference && {
                                    estate_type: EstateType[data.item_type.key],
                                    distribution_type: '2',
                                }),
                            },
                        ],
                    };
                    trackingDataList.push(eventData);
                });
            }
            this.markerPopup.setHTML(infowindow);
            this.popupCall = null;
            this.initClusterNavigation();
            $(this.markerPopup.getElement()).trigger({
                type: MAEvent.mapFeatureInfoWindowOpened,
                feature,
            });
        });
    }

    initClusterNavigation() {
        let currentPage = 1;
        const $infoWindow = this.$wrapper.find('[data-infowindow-cluster]');
        if ($infoWindow.length === 0) {
            return;
        }
        const hiddenClass = $infoWindow.data('hidden-class');
        const $pages = $infoWindow.find('[data-infowindow-cluster-item]');
        const $currentPage = $infoWindow.find('[data-current-page]');
        const navPrevDisabled = $infoWindow.data('nav-prev-disabled-class');
        const navNextDisabled = $infoWindow.data('nav-next-disabled-class');
        const $pageCount = $infoWindow.find('[data-page-count]');
        const $prevButton = $infoWindow.find('[data-cluster-prev]');
        const $nextButton = $infoWindow.find('[data-cluster-next]');
        const pageCount = $pages.length;
        $pageCount.html(pageCount);

        $infoWindow.on('click', event => {
            if (event.target.nodeName === 'A') {
                thorPushEvent('view_item_list', trackingDataList[currentPage - 1]);
            }
        });

        const changePage = offset => {
            if (offset !== 1 && offset !== -1) {
                throw new Error('`changePage` argument should be `1` or `-1`');
            }
            if (currentPage === 1 && offset === -1) {
                return;
            }
            if (currentPage === pageCount && offset === 1) {
                return;
            }
            currentPage += offset;
            $currentPage.html(currentPage);
            $pages.each(index => {
                if (index === currentPage - 1) {
                    $pages.eq(index).removeClass(hiddenClass);
                } else {
                    $pages.eq(index).addClass(hiddenClass);
                }
            });
            $prevButton.removeClass(navPrevDisabled);
            $nextButton.removeClass(navNextDisabled);
            if (currentPage === 1) {
                $prevButton.addClass(navPrevDisabled);
            }
            if (currentPage === pageCount) {
                $nextButton.addClass(navNextDisabled);
            }
            thorPushEvent('view_item_list', trackingDataList[currentPage - 1]);
        };

        $prevButton.on('click', event => {
            event.preventDefault();
            changePage(-1);
        });

        $nextButton.on('click', event => {
            event.preventDefault();
            changePage(1);
        });
    }

    /**
     * When user leave a feature marker, hide popup
     */
    hideMarkerInfoWindow() {
        this.map.getCanvas().style.cursor = '';
        if (this.markerPopup) {
            this.markerPopup.remove();
            this.markerPopup = null;
        }
        this.updateInfoWindowContent(null);
    }

    showListAddressInfoWindow(response, lngLat) {
        if (isMobile()) {
            this.updateInfoWindowContent(addressListTemplate({ addressList: response.children }));
            return;
        }

        // Else : desktop popup
        this.map.getCanvas().style.cursor = 'pointer';
        if (!this.addressPopup) {
            this.addressPopup = new MapboxGL.Popup({
                closeButton: true,
                closeOnClick: true,
                maxWidth: 'none', // new mapbox-gl version use a default maxWidth of 240px
            });
        }

        this.addressPopup
            .setLngLat(lngLat)
            .setHTML(addressListTemplate({ addressList: response.children }))
            .addTo(this.map);
    }

    /**
     * When user leave a feature marker, hide popup and infowindow
     */
    hideListAddressInfoWindow() {
        this.map.getCanvas().style.cursor = '';
        if (this.addressPopup) {
            this.addressPopup.remove();
            this.addressPopup = null;
        }
        this.updateInfoWindowContent(null);
    }

    /**
     * Bind event to show/hide price info window
     */
    initializePriceInfoWindow() {
        if (!isMobile()) {
            return;
        }

        // Subscribe event when the navigation changed to another place
        // and display prices info window
        $(window).on(MAEvent.pricesPlaceWillChange, () => {
            this.updateInfoWindowContent(spinnerTemplate({ classes: 'spinner--dots' }), false);
        });
        $(window).on(MAEvent.pricesPlaceDidChange, event => {
            this.showPricesInfoWindow(event.place, event.placePrices);
        });
        $(window).on(MAEvent.mapPricesLayerChanged, () => {
            this.showPricesInfoWindow(this.place, this.placePrices);
        });
        this.showPricesInfoWindow(this.place, this.placePrices);
    }

    /**
     * Show prices detail under the map
     * @param {Object} place
     * @param {Object} placePrices
     */
    showPricesInfoWindow(place, placePrices) {
        if (!placePrices) {
            this.$infoWindow.hide();
            return;
        }
        this.$infoWindow.show();

        const formatPrice = price => Numeral(price).format('0,0[.]0 $');

        const isRentalType = this.priceType === RENTAL_PRICE_TYPE;
        const pricePrefix = isRentalType ? 'rental' : 'sell';
        const price = getValueFromPath(placePrices, pricePrefix);
        const hasPrice = placePrices[`has_${pricePrefix}_price`];
        const hasHousePrice = placePrices[`has_${pricePrefix}_house_price`];
        const hasApartmentPrice = placePrices[`has_${pricePrefix}_apartment_price`];

        this.updateInfoWindowContent(
            pricesInfowindowTemplate({
                place,
                isRentalType,
                apartmentPrice: formatPrice(getValueFromPath(price, 'apartment.value')),
                housePrice: formatPrice(getValueFromPath(price, 'house.value')),
                shouldShowHousePrice: hasHousePrice || !hasPrice,
                shouldShowApartmentPrice: hasApartmentPrice || !hasPrice,
            }),
            false,
        );

        this.$infoWindow.find('[data-price-info-window]').on('click', () => {
            $.event.trigger({
                type: MAEvent.pricesPlaceClicked,
            });
        });
    }

    /**
     *
     * @param {String} content
     * @param {Boolean} showCloseButton
     */
    updateInfoWindowContent(content, showCloseButton = true) {
        if (!isMobile()) {
            return;
        }
        if (!content) {
            this.showPricesInfoWindow(this.place, this.placePrices);
            return;
        }
        this.$infoWindow.html(content);
        if (showCloseButton) {
            const closeButton = $(
                '<a class="vector-map__infowindow__close"><i class="icon icon-close"></i></a>',
            );
            closeButton.on('click', event => {
                event.preventDefault();
                this.updateInfoWindowContent(null);
            });
            this.$infoWindow.append(closeButton);
        }
        this.initClusterNavigation();
    }

    deboundsTimeout = 0;

    /**
     * When bounds changed (zoom or pan)
     */
    onBoundsChanged() {
        clearTimeout(this.deboundsTimeout);
        this.deboundsTimeout = setTimeout(() => {
            // Why this condition ?
            // - Method like map.resize() trigger this event
            // - We want to ignore very very small bounds changes
            if (
                deepEqual(this.map, this.currentBounds, this.map.getBounds()) ||
                !shouldChangeBounds(this.map, this.currentBounds, this.map.getBounds())
            ) {
                return;
            }
            this.currentBounds = this.map.getBounds();
            this.waitForStylesLoaded().then(() => {
                if (
                    !this.userChangedFeatures &&
                    this.map.getZoom() >= FEATURES_ZOOM_UPPER_THRESHOLD
                ) {
                    Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
                        this.toggleFeaturesLayer(layerId, 'visible');
                    });
                }
                this.fetchAllFeatures(getBoundsString(this.map.getBounds()));
            });
        }, 500); // Increase the timeout value to decrease the number of calls
    }

    /**
     * Add layer with their source
     * Order is important, add layers from background to foreground
     *
     * https://www.mapbox.com/mapbox-gl-js/api/#map#addlayer
     * https://www.mapbox.com/mapbox-gl-js/api/#map#addsource
     */
    addLayers() {
        this.initTilesLayer();

        this.addSymbolLayer(REALTOR_FEATURE_ID, 'red');
        this.addSymbolLayer(REFERENCE_FEATURE_ID, 'blue', REALTOR_FEATURE_ID);
    }

    /**
     * Add a layer to handle features
     * @param {String} layerId Internal ID of the layer
     * @param {String} iconColor red|blue|purple
     * @param {String} before Specify a layer id to place under the layer
     */
    addSymbolLayer(layerId, iconColor, before = null) {
        this.map.addSource(layerId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
        });

        const baseLayer = {
            id: layerId,
            type: 'symbol',
            source: layerId,
            layout: {
                'icon-image': `${iconColor}-dot`,
                'icon-allow-overlap': true, // https://www.mapbox.com/mapbox-gl-js/style-spec/#layout-symbol-icon-allow-overlap
            },
        };
        this.map.addLayer(baseLayer, before);

        const layerHover = Object.assign(baseLayer, {
            id: `${layerId}-hover`,
            layout: {
                'icon-image': `${iconColor}-dot--hover`,
            },
            filter: ['==', 'id', ''],
        });
        this.map.addLayer(layerHover, before);
    }

    /**
     * Event handler when user change place context
     * @param {Object} place Place object from API
     * @param {Object} placePrices Place prices from API
     * @param {Boolean} updateMap Should redraw map
     * @param {string} avivFeatureId AVIV feature ID (from history state on back navigation)
     * @param {Boolean} isMapClick Whether this came from a map click (layers were cleared in click handler)
     */
    onPlaceChanged(place, placePrices, updateMap, avivFeatureId = null, isMapClick = false) {
        this.place = place;
        this.placePrices = placePrices;

        if (updateMap) {
            this.map.fitBounds(boundsFromBox(place.viewport));
            this.setSelection({
                layer: {
                    'source-layer': place.geotype_label,
                },
                properties: this.place,
            });

            // Force displaying features marker for discovery on desktop
            if (
                !isMobile() &&
                ['city', 'subcity', 'arrmun', 'borough', 'street', 'address'].indexOf(
                    place.geotype_label,
                ) >= 0 &&
                !this.userChangedFeatures
            ) {
                Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
                    this.toggleFeaturesLayer(layerId, 'visible');
                });
            }

            // Reload AVIV layers for the new place
            // Skip if AVIV initialization hasn't completed yet (initAvivTileSources handles initial load)
            // Skip if NONE_TYPE is selected - no price layers should be shown
            if (this.avivInitialized && this.priceType !== NONE_TYPE) {
                this.reloadAvivLayersForPlace(place, avivFeatureId);
            }
        } else if (isMapClick && this.avivInitialized && place && this.priceType !== NONE_TYPE) {
            // Map click navigation completed - reload layers for the destination place
            // Layers were already cleared in the click handler before zooming
            this.reloadAvivLayersForPlace(place, null);
        }
    }

    /**
     * Reload AVIV price layers for a new place (after URL navigation)
     * Debounced to prevent excessive reloads during rapid navigation
     * @param {Object} place - Place object from API with geotype_label and IDs
     * @param {string} avivFeatureId - AVIV feature ID (from history state on back navigation)
     */
    reloadAvivLayersForPlace(place, avivFeatureId = null) {
        // Fix #1: Debounce layer reload operations (250ms delay)
        clearTimeout(this.avivLayerReloadTimeout);
        this.avivLayerReloadTimeout = setTimeout(() => {
            this._reloadAvivLayersForPlaceImpl(place, avivFeatureId);
        }, 250);
    }

    /**
     * Internal implementation of reloadAvivLayersForPlace (called after debounce)
     */
    async _reloadAvivLayersForPlaceImpl(place, avivFeatureId = null) {
        // Determine the AVIV boundary ID
        let newBoundaryId = avivFeatureId;

        if (!newBoundaryId && place) {
            // No AVIV ID provided - need to convert from legacy MA ID
            const lookupPromise = getLegacyIdLookupPromise(place);
            const avivIdFromApi = lookupPromise ? await lookupPromise : null;
            newBoundaryId = resolveBoundaryId(avivIdFromApi, place);
        }

        if (!newBoundaryId) {
            newBoundaryId = FRANCE_BOUNDARY_ID;
        }

        // Skip if same boundary - just restore visibility (may have been hidden by NONE_TYPE)
        if (newBoundaryId === this.avivBoundaryPlaceId) {
            this.showAvivPriceLayers();
            return;
        }

        const transactionType = this._getTransactionType();

        try {
            // Fetch metadata to determine effective selection boundary before removing layers
            let { metadata, effectiveBoundaryId } = await this._fetchPriceMetadataWithCache(
                newBoundaryId,
                transactionType,
            );

            // City-level boundaries without child data fall back to parent for selection/outline
            let selectionBoundaryId = effectiveBoundaryId;
            if (cityNeedsFallback(newBoundaryId, metadata)) {
                if (effectiveBoundaryId !== newBoundaryId) {
                    // Already fell back via _fetchPriceMetadataWithCache
                    selectionBoundaryId = effectiveBoundaryId;
                } else {
                    // Need to explicitly get parent for selection boundary
                    const placeData = await getPlaceData(newBoundaryId);
                    const parentSubregion = placeData?.parents?.find(p => p.type_key === 'AD06');
                    if (parentSubregion?.id) {
                        selectionBoundaryId = parentSubregion.id;

                        // Early skip check before fetching parent metadata
                        if (selectionBoundaryId === this.avivSelectionBoundaryId) {
                            this.avivBoundaryPlaceId = newBoundaryId;
                            this.showAvivPriceLayers();
                            return;
                        }

                        // Fetch parent's metadata for new layers
                        const parentResult = await this._fetchPriceMetadataWithCache(
                            parentSubregion.id,
                            transactionType,
                        );
                        if (parentResult.metadata?.length > 0) {
                            metadata = parentResult.metadata;
                            effectiveBoundaryId = parentResult.effectiveBoundaryId;
                        }
                    }
                }
            }

            // Skip reload if selection boundary unchanged (existing layers are correct)
            if (selectionBoundaryId === this.avivSelectionBoundaryId) {
                this.avivBoundaryPlaceId = newBoundaryId;
                this.showAvivPriceLayers();
                return;
            }

            // Selection boundary changed - now commit to updating layers
            this.avivBoundaryPlaceId = newBoundaryId;
            this.avivSelectionBoundaryId = selectionBoundaryId;

            this._clearLegacyOutlineSources();

            // Clear selectable layers during reload
            this.avivSelectableLayers = getSelectableLayers([], this.avivGeoLayerIds || []);

            this._removeExistingAvivLayers();

            // Update geo selection filters with the effective selection boundary
            // This determines which areas are hoverable/clickable
            this.updateGeoSelectionFilters(selectionBoundaryId);

            // Extract selection level from the selection boundary
            const parsed = parseAvivFeatureId(selectionBoundaryId);
            this.avivSelectionLevel = parsed?.type || 'AD02';

            // Add price layers if we have data
            if (metadata && metadata.length > 0) {
                this._addAvivPriceAndSelectionLayers(
                    metadata,
                    effectiveBoundaryId,
                    transactionType,
                    this.avivBoundaryPlaceId,
                );
            }

            // Add outline for the selection boundary
            this.avivOutlineLayerIds = [];
            this._addAvivOutlineLayer(selectionBoundaryId);

            this._finalizeAvivLayerReload();
        } catch (err) {
            global.console.error('[AVIV] Failed to reload layers for place:', err);
        }
    }

    // =========================================================================
    // AVIV Layer Reload Helper Methods
    // =========================================================================

    /**
     * Clear a GeoJSON source by setting it to an empty FeatureCollection
     * @param {string} sourceName - Name of the source to clear
     */
    _clearGeoJsonSource(sourceName) {
        const source = this.map.getSource(sourceName);
        if (source) {
            source.setData({ type: 'FeatureCollection', features: [] });
        }
    }

    /**
     * Clear legacy GeoJSON outline sources
     * These are set by setSelection() but need to be cleared when using AVIV outlines
     */
    _clearLegacyOutlineSources() {
        this._clearGeoJsonSource('place-selected');
        this._clearGeoJsonSource('parcelle-selected');
    }

    /**
     * Remove existing AVIV price and outline layers
     */
    _removeExistingAvivLayers() {
        if (this.avivPriceLayerIds && this.avivPriceLayerIds.length > 0) {
            removeAvivLayers(this.map, this.avivPriceLayerIds);
            this.avivPriceLayerIds = [];
        }
        if (this.avivOutlineLayerIds && this.avivOutlineLayerIds.length > 0) {
            removeAvivLayers(this.map, this.avivOutlineLayerIds);
        }
        this.avivOutlineLayerIds = [];
        this.avivSelectedPlaceOutlineId = null;
    }

    /**
     * Fetch price metadata with cache support and parent fallback
     * @param {string} boundaryId - AVIV boundary ID
     * @param {string} transactionType - 'sell' or 'rent'
     * @returns {Promise<{metadata: Array, effectiveBoundaryId: string}>}
     */
    async _fetchPriceMetadataWithCache(boundaryId, transactionType, { cityFallback = false } = {}) {
        let metadata = [];
        let effectiveBoundaryId = boundaryId;

        // Check cache first
        const cacheKey = `${boundaryId}:${transactionType}`;
        if (this.avivPriceMetadataCache.has(cacheKey)) {
            metadata = this.avivPriceMetadataCache.get(cacheKey);
        } else {
            metadata = await getPriceLayerMetadata(transactionType, boundaryId, this.avivPriceDate);
            // Cache the result if we got data
            if (metadata && metadata.length > 0) {
                // Evict oldest entry if cache is full
                if (this.avivPriceMetadataCache.size >= this.avivPriceMetadataCacheLimit) {
                    const oldestKey = this.avivPriceMetadataCache.keys().next().value;
                    this.avivPriceMetadataCache.delete(oldestKey);
                }
                this.avivPriceMetadataCache.set(cacheKey, metadata);
            }
        }

        // Determine if we need to fall back to parent:
        // - Always fall back if no data at all
        // - If cityFallback is true, also fall back when city lacks child-level price data
        const noData = !metadata || metadata.length === 0;
        const needsFallback = noData || (cityFallback && cityNeedsFallback(boundaryId, metadata));

        if (needsFallback) {
            const placeData = await getPlaceData(boundaryId);

            if (placeData?.parents && placeData.parents.length > 0) {
                // Try parents from closest (last in array) to furthest (first)
                const parentsToTry = [...placeData.parents].reverse();
                const result = await findParentWithMetadata(
                    parentsToTry,
                    transactionType,
                    this.avivPriceDate,
                );

                if (result) {
                    metadata = result.metadata;
                    effectiveBoundaryId = result.boundaryId;
                }
            }
        }

        return { metadata, effectiveBoundaryId };
    }

    /**
     * Add AVIV price layers and geo-based selection layers
     * @param {Array} metadata - Price layer metadata
     * @param {string} effectiveBoundaryId - Effective boundary ID (may differ from original if fallback used)
     * @param {string} transactionType - 'sell' or 'rent'
     * @param {string} originalBoundaryId - Original boundary ID (before fallback)
     */
    _addAvivPriceAndSelectionLayers(
        metadata,
        effectiveBoundaryId,
        transactionType,
        originalBoundaryId,
    ) {
        this.avivLayerMetadata = metadata;
        this.avivPriceLayerIds = [];
        this.avivLayerMetadata = addPriceLayersFromMetadata(
            this.map,
            this.avivLayerMetadata,
            effectiveBoundaryId,
            transactionType,
            this.avivPriceLayerIds,
            MA_LAYER,
            effectiveBoundaryId,
            originalBoundaryId,
        );

        // Add geo-based selection layers for STU3/PARC when at city level
        const boundaryType = this.avivBoundaryPlaceId?.substring(0, 4);
        const isCityLevelBoundary = boundaryType === 'AD08' || boundaryType === 'AD09';

        if (isCityLevelBoundary && this.avivGeoMetadata) {
            const existingPriceTypes = this.avivLayerMetadata
                .map(l => l.place?.type)
                .filter(Boolean);
            addGeoWithinSelectionLayers(
                this.map,
                this.avivGeoMetadata,
                effectiveBoundaryId,
                transactionType,
                existingPriceTypes,
                this.avivPriceLayerIds,
                MA_LAYER,
            );
        }

        // Store legend data
        this.avivLegendData = extractLegendData(this.avivLayerMetadata);
    }

    /**
     * Add outline layer for a boundary
     * @param {string} boundaryId - Boundary ID to outline
     * @param {string} [typeOverride] - Optional type to use instead of deriving from boundaryId
     */
    _addAvivOutlineLayer(boundaryId, typeOverride) {
        const outlineType = typeOverride || boundaryId.substring(0, 4);
        if (this.avivGeoMetadata && Array.isArray(this.avivGeoMetadata)) {
            const geoLayer = this.avivGeoMetadata.find(l => l.type === outlineType);
            if (geoLayer) {
                addCurrentPlaceOutline(
                    this.map,
                    boundaryId,
                    geoLayer,
                    this.avivOutlineLayerIds,
                    MA_LAYER,
                );
            }
        }
    }

    /**
     * Finalize layer reload - update selectable layers, legend, and trigger repaint
     */
    _finalizeAvivLayerReload() {
        this.avivSelectableLayers = getSelectableLayers(
            this.avivPriceLayerIds || [],
            this.avivGeoLayerIds || [],
        );
        this.updateAvivMapLegend();
        this.map.triggerRepaint();
    }

    /**
     * Register GeoJSON sources and layers for place selection/hover display
     * These are used for both legacy and AVIV implementations
     */
    initTilesLayer() {
        this.map.addSource('place-hover', {
            type: 'geojson',
            maxzoom: 24,
            data: { type: 'FeatureCollection', features: [] },
        });
        this.map.addSource('place-selected', {
            type: 'geojson',
            maxzoom: 24,
            data: { type: 'FeatureCollection', features: [] },
        });
        this.map.addSource('parcelle-selected', {
            type: 'geojson',
            maxzoom: 24,
            data: { type: 'FeatureCollection', features: [] },
        });

        // Add layers to render the GeoJSON sources
        // Note: These are added without "before" parameter so they render on top
        // of AVIV tile layers (which are added later with MA_LAYER as before)
        this.map.addLayer({
            id: 'place-hover',
            type: 'line',
            source: 'place-hover',
            layout: {},
            paint: {
                'line-width': 3,
                'line-color': '#18CAE6',
                'line-opacity': 1,
            },
        });

        this.map.addLayer({
            id: 'place-selected',
            type: 'line',
            source: 'place-selected',
            layout: {},
            paint: {
                'line-width': 3,
                'line-color': '#1C90FF',
                'line-opacity': 1,
            },
        });

        this.map.addLayer({
            id: 'parcelle-selected',
            type: 'line',
            source: 'parcelle-selected',
            layout: {},
            paint: {
                'line-width': 5,
                'line-color': '#1C90FF',
                'line-opacity': 1,
            },
        });
    }

    /**
     * Display or hide a feature layer
     * @param {string} layerId
     * @param {string} forceVisibility Mapbox visibility style
     */
    toggleFeaturesLayer(layerId, forceVisibility = '') {
        const isVisible = this.currentFeaturesLayers.has(layerId);
        const $wrapper = $('[data-features-toolbar]');
        const $settingsWrapper = $('#vector-map-settings-popup');
        if (forceVisibility) {
            this.map.setLayoutProperty(layerId, 'visibility', forceVisibility);
            if (forceVisibility === 'visible') {
                this.currentFeaturesLayers.add(layerId);
                this.fetchFeatures(layerId, getBoundsString(this.map.getBounds()));
                $wrapper.find(`[name=map_control_${layerId}]`).prop('checked', true);
                $settingsWrapper.find(`[name=map_setting_${layerId}]`).prop('checked', true);
            } else {
                this.currentFeaturesLayers.delete(layerId);
                this.map.setLayoutProperty(layerId, 'visibility', 'none');
                $wrapper.find(`[name=map_control_${layerId}]`).prop('checked', false);
                $settingsWrapper.find(`[name=map_setting_${layerId}]`).prop('checked', false);
            }
        } else if (isVisible) {
            this.currentFeaturesLayers.delete(layerId);
            this.map.setLayoutProperty(layerId, 'visibility', 'none');
            $wrapper.find(`[name=map_control_${layerId}]`).prop('checked', false);
            $settingsWrapper.find(`[name=map_setting_${layerId}]`).prop('checked', false);
        } else {
            this.currentFeaturesLayers.add(layerId);
            this.map.setLayoutProperty(layerId, 'visibility', 'visible');
            this.fetchFeatures(layerId, getBoundsString(this.map.getBounds()));
            $wrapper.find(`[name=map_control_${layerId}]`).prop('checked', true);
            $settingsWrapper.find(`[name=map_setting_${layerId}]`).prop('checked', true);
        }
    }

    /**
     * Fetch data to get all feature of all layers
     * @param {String} bounds String representation of bounds. Format : SW.lat,SW.lng,NE.lat,NE.lng
     */
    fetchAllFeatures(bounds) {
        return Promise.all([
            this.fetchFeatures(REALTOR_FEATURE_ID, bounds),
            this.fetchFeatures(REFERENCE_FEATURE_ID, bounds),
        ]);
    }

    /**
     * Fetch the JSON to get all features if the layer is visible
     * @param {string} layerId
     * @param {String} bounds String representation of bounds. Format : SW.lat,SW.lng,NE.lat,NE.lng
     * @return {Promise}
     */
    fetchFeatures(layerId, bounds) {
        if (!this.currentFeaturesLayers.has(layerId)) {
            return null;
        }
        // recupération du geojson pour le layer défini par le blueprint
        return HTTPService.get(getURLForFeatures(layerId, bounds)).then(response => {
            const { data } = response;
            let features = Array.isArray(data.features) ? data.features : [];
            this.waitForStylesLoaded().then(() => {
                const sourceData = this.map.getSource(layerId);
                if (IS_IE) {
                    // No cache
                    sourceData.setData({
                        type: 'FeatureCollection',
                        features,
                    });
                    return;
                }

                if (!(layerId in this.dataSourceCache)) {
                    this.dataSourceCache[layerId] = new Map();
                }

                const cache = this.dataSourceCache[layerId];
                features.forEach(feature => {
                    const key = `${layerId}-${feature.properties.id}`;
                    if (cache.has(key)) {
                        // Feature already present
                        return;
                    }
                    cache.set(key, feature);
                });
                features = Array.from(cache.values());
                // Keep last <DATA_SOURCE_CACHE_SIZE> items in array
                const cacheSize = isMobile()
                    ? DATA_SOURCE_CACHE_SIZE_MOBILE
                    : DATA_SOURCE_CACHE_SIZE_DESKTOP;
                if (features.length > cacheSize) {
                    features = features.splice(features.length - cacheSize, cacheSize);
                }
                sourceData.setData({ type: 'FeatureCollection', features });
            });
        });
    }

    /**
     * Get content to fill popup on over
     * @param {String} feature
     * @return {Promise}
     */
    fetchPopupContent(feature) {
        return HTTPService.get(this.getURLForFeature(feature), {
            cancelToken: new CancelToken(c => {
                // An executor function receives a cancel function as a parameter
                this.cancelPopupRequest = c;
            }),
        });
    }

    /*
     * Build UI of the toggle to switch visibility of layers
     */
    createToggleFeatures() {
        const $toggleContainer = $('[data-features-toolbar]');
        let innerHTML = '';
        Object.keys(this.toggleableFeaturesLayers || {}).forEach(layerId => {
            const layer = this.toggleableFeaturesLayers[layerId];
            const tpl = featuresControlTemplate({
                description: layer.tilejson.description,
                name: layer.tilejson.name,
                checked: false,
            });
            innerHTML += tpl;
        });
        $toggleContainer.html(innerHTML);

        // Bind change event
        $toggleContainer.find('input').on('change', event => {
            event.preventDefault();
            this.userChangedFeatures = true;
            this.toggleFeaturesLayer(event.target.value);
        });

        $toggleContainer.find('input').on('click', event => {
            this.$container.trigger({
                type: MAEvent.mapFeaturesLayerChanged,
                layerName: event.target.value,
            });
        });
    }

    /*
     * Build UI for switch tile layer and add it to DOM
     */
    createToggleTiles() {
        const $toggleContainer = $('[data-tile-toolbar]');
        const tpl = tilesControlTemplate({
            layers: this.toggleableTilesLayers,
            selected: this.priceType,
        });
        $toggleContainer.html(tpl);

        // Bind change event
        $toggleContainer.find('input').on('change', event => {
            event.preventDefault();
            this.switchTilesLayer(event.target.value);
        });

        // Create the "Pas de légende" element for NONE_TYPE (matches legacy updateLegends behavior)
        if ($(`[data-legend-${NONE_TYPE}]`).length === 0) {
            const $legendDom = $('<div />', {
                [`data-legend-${NONE_TYPE}`]: '',
                class: 'vector-map__no-legend',
            });
            $legendDom.hide();
            $legendDom.html(this.$mapLegend.data('no-legend-label'));
            this.$mapLegend.append($legendDom);
        }
    }

    async switchTilesLayer(newPriceType) {
        const $settingsWrapper = $('#vector-map-settings-popup');
        const $toggleContainer = $('[data-tile-toolbar]');

        Object.keys(this.toggleableTilesLayers || {}).forEach(id => {
            this.$mapLegend.find(`[data-legend-${id}]`).hide();
        });

        if (newPriceType === NONE_TYPE) {
            this.hideAvivPriceLayers();
            this.$mapLegend.find(`[data-legend-${newPriceType}]`).show();
        } else if (newPriceType !== this.priceType) {
            const wasNoneType = this.priceType === NONE_TYPE;
            // Update priceType BEFORE switching so legend gets correct description
            this.priceType = newPriceType;

            if (wasNoneType) {
                // Switching FROM NONE_TYPE - need to reload layers for current place
                // (boundary may have changed while NONE_TYPE was selected)
                this.reloadAvivLayersForPlace(this.place, null);
            } else {
                // Switching between price types - just change transaction type
                await this.switchAvivTransactionType(newPriceType);
            }
        }

        // Update dropdown button text
        $('[data-map-layer-btn]').html(
            this.toggleableTilesLayers[newPriceType]?.tilejson?.description || 'Aucun',
        );

        // Update controls view
        $toggleContainer
            .find('[name=map_control_tiles]')
            .filter(`[value=${newPriceType}]`)
            .prop('checked', true);
        $settingsWrapper
            .find('[name=map_settings_tiles]')
            .filter(`[value=${newPriceType}]`)
            .prop('checked', true);

        // Always show the legend - "Pas de légende" text is shown for NONE_TYPE
        this.$mapLegend.show();

        this.priceType = newPriceType;
        this.$container.trigger({
            type: MAEvent.mapPricesLayerChanged,
            layerName: newPriceType,
        });
    }

    /*
     * Build UI of the settings popup on mobile to switch visibility of layers
     */
    createPopupSettingsFeatures() {
        const $tilesToggleContainer = $('[data-map-settings-tiles]');
        $tilesToggleContainer.after(
            popupSettingsTilesTemplate({
                layers: this.toggleableTilesLayers,
                selected: this.priceType,
            }),
        );

        const $featuresToggleContainer = $('[data-map-settings-features]');
        $featuresToggleContainer.after(
            popupSettingsFeaturesTemplate({
                layers: this.toggleableFeaturesLayers,
                selected: this.currentFeaturesLayers,
            }),
        );

        // Bind change event
        const $container = $('[data-map-settings]');
        $container.find('[data-map-settings-tiles-control]').on('change', event => {
            event.preventDefault();
            this.switchTilesLayer(event.target.value);
        });
        $container.find('[data-map-settings-features-control]').on('change', event => {
            event.preventDefault();
            this.userChangedFeatures = true;
            this.toggleFeaturesLayer(event.target.value);
        });
    }

    /**
     * Add a button to toggle the satellite style
     * It will be add in mapbox control container
     */
    addSatelliteButton() {
        const $button = $('<div />', {
            class: 'mapboxgl-ctrl mapboxgl-ctrl-group',
        });

        $button.append(
            '<button class="mapboxgl-ctrl-icon mapboxgl-ctrl-satellite" data-map-satellite-control><i class="icon icon-public" /></button>',
        );

        $button.on('click', event => {
            event.preventDefault();
            this.toggleSatelliteStyle(true);
        });

        this.$container.find('.mapboxgl-ctrl-top-right').append($button);
    }

    toggleSatelliteStyle(forceStyleToggle = false) {
        if (this.forceStyleToggle === true && forceStyleToggle === false) {
            return;
        }
        if (forceStyleToggle === true) {
            this.forceStyleToggle = forceStyleToggle;
        }

        this.satelliteStyleActive = !this.satelliteStyleActive;
        this.$container
            .find('[data-map-satellite-control]')
            .children('.icon')
            .toggleClass('icon-public icon-map2');

        const { layers } = this.map.getStyle();
        let maLayerIndex = null;
        layers.forEach((layer, index) => {
            if (layer.id === MA_LAYER) {
                maLayerIndex = index;
            }
        });

        layers.forEach((layer, index) => {
            // do not modify dynamic GeoJSON layers (MA_LAYERS) or AVIV tile layers
            const isBeforeMA = maLayerIndex > index;
            const isMALayer = MA_LAYERS.indexOf(layer.source) !== -1;
            const isAvivLayer = isAvivLayerSource(layer.source);

            if (!isMALayer && !isAvivLayer && isBeforeMA) {
                if (layer.id === 'satellite') {
                    this.map.setLayoutProperty(
                        'satellite',
                        'visibility',
                        this.satelliteStyleActive ? 'visible' : 'none',
                    );
                } else {
                    this.map.setLayoutProperty(
                        layer.id,
                        'visibility',
                        this.satelliteStyleActive ? 'none' : 'visible',
                    );
                }
            }
        });
    }

    setStyle(...args) {
        this.styleIsLoading = true;
        this.map.setStyle(...args);
        this.map.on('style.load', () => {
            this.styleIsLoading = false;
        });
    }

    /**
     * Build URL to get the infowindow content
     * Calculate bounds around the feature to grab other features at same coordinates
     * @param {String} feature
     * @return {String} Url for fetching feature popup content
     */
    getURLForFeature(feature) {
        const latOffset = 0.00003;
        const lngOffset = 0.00004;
        const { lng, lat } = JSON.parse(feature.properties.coordinates);
        const bounds = getBoundsString(
            new MapboxGL.LngLatBounds(
                new MapboxGL.LngLat(lng - lngOffset, lat + latOffset),
                new MapboxGL.LngLat(lng + lngOffset, lat - latOffset),
            ),
        );

        return urlJoin(
            '/carte/infowindow',
            `?bounds=${bounds}`,
            `&ga_category=${this.$container.data('ua-event-category')}`,
            `&ga_label_prefix=${this.$container.data('ua-event-label-prefix')}`,
            `&wa_name_prefix=${this.$container.data('wa-name-prefix')}`,
            `&wa_source=${this.$container.data('wa-source')}`,
            `&id=${feature.properties.id}`,
            `&tracking_data=true`,
        );
    }

    /**
     * Bind event to Google Analytics events.
     * Assume the map is loaded
     */
    setTracking() {
        const waPageviewData = Wa.computeData(MA.Context.waData);

        $(window).on('gaSendFitBounds', event => {
            Ga.send({
                hitType: 'event',
                eventCategory: this.$container.data('ua-event-category'),
                eventAction: `go:prices:${event.placeType}`,
                eventLabel: 'prices:map:click',
            });
        });

        if (!isMobile()) {
            this.map.on('click', `${REFERENCE_FEATURE_ID}-hover`, event => {
                const feature = parseStringifiedFeatures(event.features[0]);
                const { id, realtor } = feature.properties;
                Ga.send({
                    hitType: 'event',
                    eventCategory: this.$container.data('ua-event-category'),
                    eventAction: 'go:past_sale',
                    eventLabel: `${this.$container.data(
                        'ua-event-label-prefix',
                    )}:past_sales:marker`,
                });
                Wa.push(
                    'go:past_sale',
                    `${this.$container.data('wa-name-prefix')}:past_sales`,
                    `source=${this.$container.data('wa-source')}|realtor_id=${
                        realtor.id
                    }|reference_id=${id}${waPageviewData}`,
                );
            });
            this.map.on('click', `${REALTOR_FEATURE_ID}-hover`, event => {
                Ga.send({
                    hitType: 'event',
                    eventCategory: this.$container.data('ua-event-category'),
                    eventAction: 'go:realtor_page',
                    eventLabel: `${this.$container.data('ua-event-label-prefix')}:realtors:marker`,
                });
                Wa.push(
                    'go:realtor',
                    `${this.$container.data('wa-name-prefix')}:realtors`,
                    `source=${this.$container.data('wa-source')}|realtor_id=${
                        event.features[0].properties.id
                    }${waPageviewData}`,
                );
            });
        }

        this.$wrapper.on(
            [MAEvent.mapPricesLayerChanged, MAEvent.mapFeaturesLayerChanged].join(' '),
            event => {
                Ga.send({
                    hitType: 'event',
                    eventCategory: MA.Context.mapGaCategory,
                    eventAction: `${MA.Context.mapGaLabelPrefix}:switch_layer:${event.layerName}`,
                    eventLabel: `${MA.Context.mapGaLabelPrefix}:switch_layer:${event.layerName}`,
                });
            },
        );

        this.$wrapper.on(MAEvent.mapFeatureInfoWindowOpened, event => {
            const feature = parseStringifiedFeatures(event.feature);
            const featureType = feature.source;

            // We recompute WA data cause place_type may have changed
            const updatedWaPageviewData = Wa.computeData(MA.Context.waData);

            switch (featureType) {
                case REFERENCE_FEATURE_ID:
                    Ga.send({
                        hitType: 'event',
                        eventCategory: this.$container.data('ua-event-category'),
                        eventAction: `${this.$container.data(
                            'ua-event-label-prefix',
                        )}:past_sales:infowindow:open`,
                        eventLabel: `${this.$container.data(
                            'ua-event-label-prefix',
                        )}:past_sales:marker`,
                    });
                    Wa.push(
                        'print:past_sale',
                        `${this.$container.data('wa-name-prefix')}:past_sales`,
                        `source=${this.$container.data('wa-source')}|realtor_id=${
                            feature.properties.realtor.id
                        }|reference_id=${feature.properties.id}${updatedWaPageviewData}`,
                    );
                    break;
                case REALTOR_FEATURE_ID:
                    Ga.send({
                        hitType: 'event',
                        eventCategory: this.$container.data('ua-event-category'),
                        eventAction: `${this.$container.data(
                            'ua-event-label-prefix',
                        )}:realtors:infowindow:open`,
                        eventLabel: `${this.$container.data(
                            'ua-event-label-prefix',
                        )}:realtors:marker`,
                    });
                    Wa.push(
                        'print:realtor',
                        `${this.$container.data('wa-name-prefix')}:realtors`,
                        `source=${this.$container.data('wa-source')}|realtor_id=${
                            feature.properties.id
                        }${updatedWaPageviewData}`,
                    );
                    break;
                default:
                    global.console.warn(`Unknown feature type ${featureType}`);
            }
        });

        this.$container.on('click', '.mapboxgl-ctrl-zoom-in', () => {
            Ga.send({
                hitType: 'event',
                eventCategory: this.$container.data('ua-event-category'),
                eventAction: `${this.$container.data('ua-event-label-prefix')}:zoom_in`,
                eventLabel: `${this.$container.data('ua-event-label-prefix')}:zoom_in_button`,
            });
        });

        this.$container.on('click', '.mapboxgl-ctrl-zoom-out', () => {
            Ga.send({
                hitType: 'event',
                eventCategory: this.$container.data('ua-event-category'),
                eventAction: `${this.$container.data('ua-event-label-prefix')}:zoom_out`,
                eventLabel: `${this.$container.data('ua-event-label-prefix')}:zoom_out_button`,
            });
        });
    }

    setSelection(feature, parcelleSelected = false) {
        this.map.getSource('place-hover').setData({ type: 'FeatureCollection', features: [] });
        this.map.getSource('place-selected').setData({ type: 'FeatureCollection', features: [] });
        if (!parcelleSelected) {
            this.map
                .getSource('parcelle-selected')
                .setData({ type: 'FeatureCollection', features: [] });
        }

        const placeType = feature.properties.geotype_label;

        if (SELECTED_FEATURE_GEO_TYPE.indexOf(placeType) >= 0) {
            if (placeType === 'address') {
                this._showPlaceOutline('address', feature.properties.id);
            }
            this.setSelection(
                {
                    layer: {
                        'source-layer': 'city',
                    },
                    properties: {
                        id: feature.properties.city_id,
                        geotype_label: 'city',
                    },
                },
                true,
            );
            return;
        }
        if (placeType === 'subcity') {
            this.setSelection({
                layer: {
                    'source-layer': 'city',
                },
                properties: {
                    id: feature.properties.city_id,
                    geotype_label: 'city',
                },
            });
        }
    }

    /**
     * Some tasks cannont be done while style is loading
     * So this method can defer execution of a function if the style is loading
     * or immediatly if not
     */
    waitForStylesLoaded() {
        return new Promise(resolve => {
            const listener = event => {
                if (event) {
                    this.map.off('style.load', listener);
                }
                resolve({});
            };
            if (this.styleIsLoading) {
                this.map.on('style.load', listener);
            } else {
                listener();
            }
        });
    }

    toggleSatelliteStyleOnZoom() {
        if (!this.satelliteStyleActive && this.map.getZoom() >= MAP_SATELLITE_MIN_ZOOM) {
            this.toggleSatelliteStyle();
        } else if (this.satelliteStyleActive && this.map.getZoom() < MAP_SATELLITE_MIN_ZOOM) {
            this.toggleSatelliteStyle();
        }
    }

    // ==========================================
    // AVIV Tile Sources Methods
    // ==========================================

    /**
     * Get the AVIV transaction type based on current price type
     * @returns {string} 'sell' or 'rent'
     */
    _getTransactionType() {
        return this.priceType === RENTAL_PRICE_TYPE ? 'rent' : 'sell';
    }

    /**
     * Update AVIV hover visualization
     * Shows outline around the hovered feature using filter-based visibility.
     *
     * @param {Object|null} feature - The hovered feature or null to clear
     */
    updateAvivHover(feature) {
        this.lastAvivHoverLayerId = updateHoverState(this.map, feature, this.lastAvivHoverLayerId);
    }

    /**
     * Initialize AVIV tile sources
     * Uses both tile-api (geometry) and price-tile-api (colors) like price-pages does
     * Fix #2: Parallelizes independent API calls for faster initialization
     */
    async initAvivTileSources() {
        try {
            // Initialize tracking arrays
            this.avivGeoLayerIds = [];
            this.avivPriceLayerIds = [];
            this.avivOutlineLayerIds = [];
            this.avivSelectedPlaceOutlineId = null;

            // Parallelize independent API calls for faster initialization
            const parallelPromises = [getGeoLayerMetadata(), getActivePriceDate()];

            // Add legacy ID lookup to parallel calls if needed
            const legacyIdPromise = getLegacyIdLookupPromise(this.place);
            if (legacyIdPromise) {
                parallelPromises.push(legacyIdPromise);
            }

            // Execute all API calls in parallel
            const results = await Promise.all(parallelPromises);

            // Extract results
            const [geoLayerMetadata, priceDate, avivIdFromApi = null] = results;
            this.avivPriceDate = priceDate;
            this.avivGeoMetadata = geoLayerMetadata;

            // Determine boundary using shared resolution logic
            this.avivBoundaryPlaceId = resolveBoundaryId(avivIdFromApi, this.place);

            // Add geo layers for "around" selection (areas outside current boundary)
            if (this.avivGeoMetadata.length > 0) {
                addGeoSelectionLayers(
                    this.map,
                    this.avivGeoMetadata,
                    this.avivBoundaryPlaceId,
                    this.avivGeoLayerIds,
                    MA_LAYER,
                );
            }

            // Extract selection level from boundary ID (e.g., "AD08FR75056" -> "AD08")
            const parsed = parseAvivFeatureId(this.avivBoundaryPlaceId);
            const selectionLevel = parsed?.type || 'AD02';
            this.avivSelectionLevel = selectionLevel;

            // Fetch price layer metadata from price-tile-api (with cache and city fallback)
            const transactionType = this._getTransactionType();
            const { metadata, effectiveBoundaryId } = await this._fetchPriceMetadataWithCache(
                this.avivBoundaryPlaceId,
                transactionType,
                { cityFallback: true },
            );

            // Add price layers and geo-within selection layers (helper also extracts legend data)
            if (metadata && metadata.length > 0) {
                this._addAvivPriceAndSelectionLayers(
                    metadata,
                    effectiveBoundaryId,
                    transactionType,
                    this.avivBoundaryPlaceId,
                );
            } else {
                this.avivLayerMetadata = metadata || [];
                this.avivLegendData = null;
            }

            // Set selection boundary ID (may differ from avivBoundaryPlaceId when fallback was used)
            // This is used to skip unnecessary layer reloads when selection boundary doesn't change
            this.avivSelectionBoundaryId = effectiveBoundaryId;

            // Update geo selection filters with the effective boundary
            // This is needed when fallback was used - geo layers were added with original boundary filter
            // but should exclude the effective (parent) boundary instead
            if (effectiveBoundaryId !== this.avivBoundaryPlaceId) {
                this.updateGeoSelectionFilters(effectiveBoundaryId);
            }

            // Add outline for the effective boundary (parent if fallback was used)
            this._addAvivOutlineLayer(effectiveBoundaryId);

            // If we fell back to a parent boundary, clear the legacy place-selected source
            // to avoid showing two outlines (legacy outline for original place + AVIV outline for parent)
            if (effectiveBoundaryId !== this.avivBoundaryPlaceId) {
                this._clearLegacyOutlineSources();
            }

            // If we fell back to parent and this is an address/parcelle/building,
            // show the parcel/building outline
            const placeGeotype = this.place?.geotype_label;
            const placeId = this.place?.legacy_id || this.place?.id;
            const isAddressOrParcelle =
                placeGeotype === 'address' ||
                placeGeotype === 'parcelle' ||
                placeGeotype === 'building';

            if (
                effectiveBoundaryId !== this.avivBoundaryPlaceId &&
                isAddressOrParcelle &&
                placeId
            ) {
                this._showPlaceOutline(placeGeotype, placeId);
            }

            // Update selectable layers for click/hover
            this.avivSelectableLayers = getSelectableLayers(
                this.avivPriceLayerIds,
                this.avivGeoLayerIds,
            );

            this.updateAvivMapLegend();

            // Force Mapbox to re-evaluate visible tiles after layers are added
            this.map.triggerRepaint();

            this.avivInitialized = true;
        } catch (err) {
            // Failed to initialize AVIV tiles - keep avivInitialized false to skip layer updates
            global.console.error('[AVIV] initAvivTileSources failed:', err);
            this.avivInitialized = false;
        }
    }

    /**
     * Update geo selection layer filters when boundary changes.
     * The geo selection layers filter out the current boundary to prevent
     * clicking/hovering on the currently selected place. When the boundary
     * changes, we need to update these filters to exclude the NEW boundary
     * instead of the old one.
     *
     * @param {string} newBoundaryId - The new boundary place ID (e.g., AD04FR11)
     */
    updateGeoSelectionFilters(newBoundaryId) {
        if (!this.avivGeoLayerIds || this.avivGeoLayerIds.length === 0) {
            return;
        }

        // Update filter on all geo selection -fill and -border layers
        // to exclude the NEW boundary instead of the old one
        const updatedLayers = [];
        this.avivGeoLayerIds.forEach(layerId => {
            if (layerId.endsWith('-fill') || layerId.endsWith('-border')) {
                if (this.map.getLayer(layerId)) {
                    this.map.setFilter(layerId, [
                        'all',
                        ['!=', ['get', 'feature_id'], newBoundaryId],
                    ]);
                    updatedLayers.push(layerId);
                }
            }
        });
    }

    /**
     * Show parcel/building outline for a place.
     *
     * @param {string} geotype - Place type (address, parcelle, building)
     * @param {number|string} legacyId - MA place ID
     */
    _showPlaceOutline(geotype, legacyId) {
        const avivType = geotype === 'building' ? 'BILD' : 'PARC';
        getAvivIdFromLegacy(geotype, legacyId)
            .then(avivId => {
                if (!avivId) return;
                if (geotype === 'address') {
                    // For addresses, get the parent parcel
                    getPlaceData(avivId).then(placeData => {
                        const parcelId = placeData?.parents?.find(p => p.type_key === 'PARC')?.id;
                        if (parcelId) {
                            this.updateSelectedPlaceOutline(parcelId, 'PARC');
                        }
                    });
                } else {
                    // For parcelle/building, use the ID directly
                    this.updateSelectedPlaceOutline(avivId, avivType);
                }
            })
            .catch(err => {
                global.console.error('[Map] Failed to show place outline:', err);
            });
    }

    /**
     * Update the selected place outline when clicking on a parcelle/building.
     * This removes the old parcelle outline and adds a new one for the clicked place,
     * while keeping the parent boundary outline intact.
     *
     * @param {string} featureId - The clicked feature ID (e.g., PARCFR12345)
     * @param {string} avivType - The AVIV type (e.g., PARC, BILD)
     */
    updateSelectedPlaceOutline(featureId, avivType) {
        // Remove the previous selected place outline if it exists
        this._removeSelectedPlaceOutline();

        // Clear any legacy parcelle-selected source
        this._clearGeoJsonSource('parcelle-selected');

        // Add new outline for the clicked feature
        const geoLayer = this.avivGeoMetadata?.find(l => l.type === avivType);
        if (geoLayer) {
            // Track the layers before adding
            const layersBefore = [...(this.avivOutlineLayerIds || [])];

            addCurrentPlaceOutline(
                this.map,
                featureId,
                geoLayer,
                this.avivOutlineLayerIds,
                MA_LAYER,
            );

            // Find the newly added layer ID
            const newLayerId = (this.avivOutlineLayerIds || []).find(
                id => !layersBefore.includes(id),
            );
            if (newLayerId) {
                this.avivSelectedPlaceOutlineId = newLayerId;
            }
        }
    }

    /**
     * Remove the currently selected place outline (parcelle/building) if it exists.
     * Cleans up both the layer and the tracking state.
     */
    _removeSelectedPlaceOutline() {
        if (!this.avivSelectedPlaceOutlineId) return;
        removeAvivLayers(this.map, [this.avivSelectedPlaceOutlineId]);
        this.avivOutlineLayerIds = (this.avivOutlineLayerIds || []).filter(
            id => id !== this.avivSelectedPlaceOutlineId,
        );
        this.avivSelectedPlaceOutlineId = null;
    }

    /**
     * Set visibility on an array of layer IDs
     */
    _setLayerVisibility(layerIds, visibility) {
        layerIds?.forEach(layerId => {
            if (this.map.getLayer(layerId)) {
                this.map.setLayoutProperty(layerId, 'visibility', visibility);
            }
        });
    }

    /**
     * Hide AVIV price and outline layers (not geo - needed for hover).
     * Used during navigation to prevent flicker while zooming.
     */
    hideAvivPriceLayers() {
        this._setLayerVisibility(this.avivPriceLayerIds, 'none');
        this._setLayerVisibility(this.avivOutlineLayerIds, 'none');
        hideAvivLegend(this.$mapLegend);
    }

    /**
     * Show AVIV price and outline layers (restore after hiding).
     * Used when navigation falls back to the same selection boundary.
     */
    showAvivPriceLayers() {
        this._setLayerVisibility(this.avivPriceLayerIds, 'visible');
        this._setLayerVisibility(this.avivOutlineLayerIds, 'visible');
        this.updateAvivMapLegend();
    }

    /**
     * Switch AVIV layers when price type changes (sale <-> rental)
     * Falls back to parent boundary if data is not available for current boundary.
     * @param {string} newPriceType - The new price type (estimates or rental_price)
     */
    async switchAvivTransactionType(newPriceType) {
        const newTransactionType = newPriceType === RENTAL_PRICE_TYPE ? 'rent' : 'sell';

        // Remove old price layers and clear selectable layers immediately
        // (before async fetch to prevent mousemove errors on deleted layers)
        if (this.avivPriceLayerIds && this.avivPriceLayerIds.length > 0) {
            removeAvivLayers(this.map, this.avivPriceLayerIds);
            this.avivPriceLayerIds = [];
        }
        this.avivSelectableLayers = [];

        // Clear selected place outline if switching to RENT
        // RENT mode doesn't support BILD, PARC, or BLOC selection (only STU3 and admin boundaries)
        if (newTransactionType === 'rent') {
            // Clear any legacy parcelle-selected source
            this._clearGeoJsonSource('parcelle-selected');

            // Also clear avivSelectedPlaceOutlineId if it's an invalid type for RENT
            if (this.avivSelectedPlaceOutlineId) {
                // Extract place type from layer ID (format: current-place-outline-{type}-{placeId})
                const layerIdParts = this.avivSelectedPlaceOutlineId.split('-');
                const selectedType = layerIdParts[3]; // e.g., "BILD", "PARC", "BLOC"

                if (INVALID_RENT_PLACE_TYPES.includes(selectedType)) {
                    this._removeSelectedPlaceOutline();
                }
            }

            // Also clear outline layers with invalid types for RENT (BILD, PARC, BLOC)
            if (this.avivOutlineLayerIds?.length > 0) {
                // Extract place type from layer ID (format: current-place-outline-{name}-{placeId})
                const getPlaceType = layerId => {
                    const match = layerId.match(/current-place-outline-[^-]+-(.+)/);
                    const placeId = match ? match[1] : null;
                    return placeId ? placeId.substring(0, 4) : null;
                };

                const layersToRemove = this.avivOutlineLayerIds.filter(layerId => {
                    const placeType = getPlaceType(layerId);
                    return placeType && INVALID_RENT_PLACE_TYPES.includes(placeType);
                });

                if (layersToRemove.length > 0) {
                    removeAvivLayers(this.map, layersToRemove);
                    this.avivOutlineLayerIds = this.avivOutlineLayerIds.filter(
                        id => !layersToRemove.includes(id),
                    );
                    // Clear selected place outline ID if it was removed
                    if (layersToRemove.includes(this.avivSelectedPlaceOutlineId)) {
                        this.avivSelectedPlaceOutlineId = null;
                    }
                }
            }
        }

        try {
            // Fetch new metadata for the new transaction type (with cache and city fallback)
            const { metadata } = await this._fetchPriceMetadataWithCache(
                this.avivBoundaryPlaceId,
                newTransactionType,
                { cityFallback: true },
            );

            // Add new price layers (helper also extracts legend data)
            if (metadata && metadata.length > 0) {
                this._addAvivPriceAndSelectionLayers(
                    metadata,
                    this.avivSelectionBoundaryId, // Use existing selection boundary (don't change on type switch)
                    newTransactionType,
                    this.avivBoundaryPlaceId,
                );

                // Update selectable layers
                this.avivSelectableLayers = getSelectableLayers(
                    this.avivPriceLayerIds,
                    this.avivGeoLayerIds || [],
                );

                // Update geo selection filters with the selection boundary
                // This ensures AD08 hover works when at city level with fallback to parent
                this.updateGeoSelectionFilters(this.avivSelectionBoundaryId);

                // Ensure outline and geo layers are visible (may have been hidden by "Aucun")
                this._setLayerVisibility(this.avivOutlineLayerIds, 'visible');
                this._setLayerVisibility(this.avivGeoLayerIds, 'visible');

                // Move outline layers to top so they render above price/selection layers
                // This is needed because price layers were just re-added and may be on top
                this.avivOutlineLayerIds?.forEach(layerId => {
                    if (this.map.getLayer(layerId)) {
                        this.map.moveLayer(layerId);
                    }
                });

                this.updateAvivMapLegend();
            } else {
                // No layers returned - clear legend
                this.avivLayerMetadata = metadata || [];
                this.avivLegendData = null;
                hideAvivLegend(this.$mapLegend);
            }
        } catch (err) {
            // API error (e.g., rent data not available for this boundary)
            // Clear metadata and legend (selectable layers already cleared above)
            this.avivLayerMetadata = [];
            this.avivLegendData = null;
            hideAvivLegend(this.$mapLegend);
            global.console.warn(
                '[AVIV] Failed to load price data for transaction type:',
                newTransactionType,
                err,
            );
        }
    }

    /**
     * Update the AVIV map legend using stored boundary legend data
     * The legend does NOT change on zoom - only when a new boundary is selected
     * This matches legacy behavior where legend is tied to the selected place
     */
    updateAvivMapLegend() {
        if (!this.avivLegendData || !this.$mapLegend) {
            return;
        }

        const description = this.toggleableTilesLayers[this.priceType]?.tilejson?.description || '';
        updateAvivLegend(this.$mapLegend, this.avivLegendData, description);
    }
}
